import {objectFromEntries} from '@benchling/js-utils';
import {Edge, Node, NodeOrigin} from 'reactflow';

import {DEFAULT_LAYOUT_ALGORITHM, LayoutAlgorithmName} from './algorithms';

/**
 * Settings for arranging a flowchart.
 */
export interface LayoutSettings<N extends Node, E extends Edge> {
  calculateNodeDimensions: (node: N) => {height: number; width: number};
  calculateEdgeLabelDimensions: (edge: E) => {height: number; width: number};
  horizontalSpaceBetweenNodes: number;
  verticalSpaceBetweenNodes: number;
  nodeOrigin: NodeOrigin;
  snapToGrid?: [number, number];
  algorithm: LayoutAlgorithmName;
}

/**
 * Default settings for arranging a flowchart.
 *
 * The default settings:
 * • Use nodes `height` and `width`, as auto-calculated by `reactflow`
 * • Does not assign dimensions for edge labels
 * • Spaces nodes 100 pixels apart
 * • Node positions returned by the layout represent the top-left position of each node
 *
 * @package
 */
export const DEFAULT_LAYOUT_SETTINGS: LayoutSettings<Node, Edge> = {
  /** Use nodes' heights and widths that were automatically calculated by `reactflow`. */
  calculateNodeDimensions: (node) => ({height: node.height ?? 0, width: node.width ?? 0}),
  // TODO(BNCH-125315): Automatically calculate edge labels' dimensions
  calculateEdgeLabelDimensions: () => ({height: 0, width: 0}),
  horizontalSpaceBetweenNodes: 100,
  verticalSpaceBetweenNodes: 100,
  nodeOrigin: [0, 0],
  algorithm: DEFAULT_LAYOUT_ALGORITHM,
};

/**
 * Calculated dimensions to layout a flowchart.
 *
 * See `LayoutSettings` and `calculateDimensionsForLayout` to calculate these dimensions.
 *
 * @package
 */
export interface DimensionsForLayout {
  /** Map from nodes' IDs to dimensions. A dimension must be calculated for each node. */
  nodes: {[nodeId: string]: {height: number; width: number}};
  /** Map from edges' IDs to their labels' dimensions. A dimension must be calculated for each edge. */
  edges: {[edgeId: string]: {height: number; width: number}};
  horizontalSpaceBetweenNodes: number;
  verticalSpaceBetweenNodes: number;
  nodeOrigin: NodeOrigin;
  snapToGrid?: [number, number];
}

/**
 * Calculate all dimensions required by `layoutFlowchart`.
 *
 * @package
 */
export default function calculateDimensionsForLayout<N extends Node, E extends Edge>(
  nodes: Array<N>,
  edges: Array<E>,
  {
    calculateNodeDimensions,
    calculateEdgeLabelDimensions,
    horizontalSpaceBetweenNodes,
    verticalSpaceBetweenNodes,
    nodeOrigin,
    snapToGrid,
  }: LayoutSettings<N, E>
): DimensionsForLayout {
  return {
    nodes: objectFromEntries(nodes.map((node) => [node.id, calculateNodeDimensions(node)])),
    edges: objectFromEntries(edges.map((edge) => [edge.id, calculateEdgeLabelDimensions(edge)])),
    horizontalSpaceBetweenNodes,
    verticalSpaceBetweenNodes,
    nodeOrigin,
    snapToGrid,
  };
}
